import PropTypes from "prop-types"
import React, { useState } from "react"
import {
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import SweetAlert from "react-bootstrap-sweetalert"
import { withTranslation } from "react-i18next"

import { addClass } from "./classData"

const getInitialForm = () => ({
  name: "",
  grade: "",
  homeroomTeachers: [""],
  room: "",
  academicYear: "2026/2027",
  studentCount: "0",
  capacity: "30",
  status: "Active",
  description: "",
})

const AddClass = props => {
  const [form, setForm] = useState(getInitialForm)
  const [errors, setErrors] = useState({})
  const [successAlert, setSuccessAlert] = useState(false)

  const handleChange = event => {
    const { name, value } = event.target
    setForm(current => ({ ...current, [name]: value }))
    setErrors(current => ({ ...current, [name]: "" }))
  }

  const handleTeacherChange = (index, value) => {
    setForm(current => ({
      ...current,
      homeroomTeachers: current.homeroomTeachers.map((teacher, teacherIndex) =>
        teacherIndex === index ? value : teacher
      ),
    }))
    setErrors(current => {
      const teacherErrors = [...(current.homeroomTeachers || [])]
      teacherErrors[index] = ""
      return { ...current, homeroomTeachers: teacherErrors }
    })
  }

  const addTeacherInput = () => {
    setForm(current => ({
      ...current,
      homeroomTeachers: [...current.homeroomTeachers, ""],
    }))
  }

  const removeTeacherInput = index => {
    setForm(current => ({
      ...current,
      homeroomTeachers: current.homeroomTeachers.filter(
        (teacher, teacherIndex) => teacherIndex !== index
      ),
    }))
    setErrors(current => ({
      ...current,
      homeroomTeachers: (current.homeroomTeachers || []).filter(
        (message, teacherIndex) => teacherIndex !== index
      ),
    }))
  }

  const validate = () => {
    const nextErrors = {}

    if (!form.name.trim()) nextErrors.name = "Class name is required"
    if (!form.grade) nextErrors.grade = "Grade is required"
    const teacherErrors = form.homeroomTeachers.map(teacher =>
      teacher.trim() ? "" : "Form teacher is required"
    )
    if (teacherErrors.some(Boolean)) {
      nextErrors.homeroomTeachers = teacherErrors
    }
    if (!form.room.trim()) nextErrors.room = "Room is required"
    if (!form.academicYear.trim()) {
      nextErrors.academicYear = "Academic year is required"
    }
    if (Number(form.capacity) < 1) {
      nextErrors.capacity = "Capacity must be at least 1"
    }
    if (Number(form.studentCount) < 0) {
      nextErrors.studentCount = "Student count cannot be negative"
    } else if (Number(form.studentCount) > Number(form.capacity)) {
      nextErrors.studentCount = "Student count cannot exceed class capacity"
    }

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const resetForm = () => {
    setForm(getInitialForm())
    setErrors({})
  }

  const handleClose = () => {
    resetForm()
    props.toggleModal()
  }

  const handleSubmit = event => {
    event.preventDefault()

    if (!validate()) return

    addClass({
      ...form,
      name: form.name.trim(),
      homeroomTeachers: form.homeroomTeachers.map(teacher => teacher.trim()),
      room: form.room.trim(),
      academicYear: form.academicYear.trim(),
      studentCount: Number(form.studentCount),
      capacity: Number(form.capacity),
      description: form.description.trim(),
    })

    props.onCreated?.()
    setSuccessAlert(true)
  }

  return (
    <>
      <Modal
        size="lg"
        centered={true}
        isOpen={props.modalShow}
        toggle={handleClose}
        scrollable={true}
      >
        <ModalHeader toggle={handleClose}>Add Class</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit} noValidate>
            <div className="border rounded p-3 mb-3">
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="name">Class Name</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="Example: Lotus C"
                      value={form.name}
                      onChange={handleChange}
                      invalid={Boolean(errors.name)}
                    />
                    <FormFeedback>{errors.name}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="grade">Grade</Label>
                    <Input
                      id="grade"
                      name="grade"
                      type="select"
                      value={form.grade}
                      onChange={handleChange}
                      invalid={Boolean(errors.grade)}
                    >
                      <option value="">Select grade</option>
                      {Array.from({ length: 12 }, (_, index) => (
                        <option key={index + 1} value={`Grade ${index + 1}`}>
                          Grade {index + 1}
                        </option>
                      ))}
                    </Input>
                    <FormFeedback>{errors.grade}</FormFeedback>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="room">Room</Label>
                    <Input
                      id="room"
                      name="room"
                      placeholder="Example: A-103"
                      value={form.room}
                      onChange={handleChange}
                      invalid={Boolean(errors.room)}
                    />
                    <FormFeedback>{errors.room}</FormFeedback>
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="mb-0">Form Teacher</Label>
                  <Button
                    type="button"
                    color="primary"
                    className="btn-sm"
                    onClick={addTeacherInput}
                  >
                    <i className="bx bx-plus me-1" />
                    Add Form Teacher
                  </Button>
                </div>
                {form.homeroomTeachers.map((teacher, index) => (
                  <div
                    key={`homeroom-teacher-${index}`}
                    className="d-flex align-items-start mb-2"
                  >
                    <div className="flex-grow-1">
                      <Input
                        id={`homeroomTeacher-${index}`}
                        name={`homeroomTeacher-${index}`}
                        placeholder={`Form teacher ${index + 1}`}
                        value={teacher}
                        onChange={event =>
                          handleTeacherChange(index, event.target.value)
                        }
                        invalid={Boolean(errors.homeroomTeachers?.[index])}
                      />
                      <FormFeedback>
                        {errors.homeroomTeachers?.[index]}
                      </FormFeedback>
                    </div>
                    {form.homeroomTeachers.length > 1 ? (
                      <Button
                        type="button"
                        color="danger"
                        className="btn-sm ms-2"
                        onClick={() => removeTeacherInput(index)}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </div>
                ))}
              </FormGroup>

              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="academicYear">Academic Year</Label>
                    <Input
                      id="academicYear"
                      name="academicYear"
                      placeholder="2026/2027"
                      value={form.academicYear}
                      onChange={handleChange}
                      invalid={Boolean(errors.academicYear)}
                    />
                    <FormFeedback>{errors.academicYear}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="studentCount">Number of Students</Label>
                    <Input
                      id="studentCount"
                      name="studentCount"
                      type="number"
                      min="0"
                      value={form.studentCount}
                      onChange={handleChange}
                      invalid={Boolean(errors.studentCount)}
                    />
                    <FormFeedback>{errors.studentCount}</FormFeedback>
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="capacity">Class Capacity</Label>
                    <Input
                      id="capacity"
                      name="capacity"
                      type="number"
                      min="1"
                      value={form.capacity}
                      onChange={handleChange}
                      invalid={Boolean(errors.capacity)}
                    />
                    <FormFeedback>{errors.capacity}</FormFeedback>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="status">Status</Label>
                    <Input
                      id="status"
                      name="status"
                      type="select"
                      value={form.status}
                      onChange={handleChange}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="description">Description</Label>
                    <Input
                      id="description"
                      name="description"
                      type="textarea"
                      rows="1"
                      placeholder="Optional class description"
                      value={form.description}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>

            <Row>
              <Col md="12">
                <div className="d-flex justify-content-end">
                  <Button
                    className="btn-sm"
                    color="danger"
                    type="button"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="btn-sm mx-1"
                    color="primary"
                    type="submit"
                  >
                    Simpan
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </Modal>

      {successAlert ? (
        <SweetAlert
          title="Good job!"
          confirmBtnText="Yes"
          success
          confirmBtnBsStyle="primary"
          onConfirm={() => {
            setSuccessAlert(false)
            handleClose()
          }}
        >
          Data Class has been created successfully
        </SweetAlert>
      ) : null}
    </>
  )
}

AddClass.propTypes = {
  modalShow: PropTypes.bool,
  toggleModal: PropTypes.func,
  onCreated: PropTypes.func,
}

export default withTranslation()(AddClass)
